import React from 'react';

import {showErrorModal, showModal} from '@utils/modal';
import {PackageContentTierGeoRestrictionsLayout} from '@modules/client/forms/PackageContentTierGeoRestrictionsLayout';
import {convertToInt} from '@utils/helpers';
import ContentPackageForm from '@modules/client/forms/ContentPackageForm';
import {getLink} from '@appComponents/Link';
import {GetProductEventContentVariants} from '@graphql/clientPackage/query';

import client from '../../../../apolloClient';

export const fetchProductEventContentVariants = async (productId) => {
    try {
        const {data} = await client.query({
            query: GetProductEventContentVariants,
            variables: {id: productId},
        });

        return data.product.product_event_content_variants || [];
    } catch (error) {
        showErrorModal({
            header: 'Error fetching product variants',
            text: error,
        });

        return [];
    }
};

export const handleOpenEditGlobeModal = async (data) => {
    generateLinkForModals(data, 'clients.packages.content.geoRestrictions');
    await openEditGlobeModal(data);
};

export const openEditGlobeModal = async (data) => {
    showModal({
        size: 'small',
        isVisible: true,
        className: 'client-package-content-geo-modal',
        content: <PackageContentTierGeoRestrictionsLayout
            header={`Client '${data.clientName}' - ${data.clientPackageName} - Content\n${data.parentNames.join(' > ')} > ${data.name}`}
            entityName={`${data.parentNames.join(' > ')} > ${data.name}`}
            bookingTypes={data.ContentTreeData.bookingTypes}
            eventContentVariants={data.ContentTreeData.eventContentVariants}
            productVariants={await fetchProductEventContentVariants(data.clientProduct.product.id)}
            product={data.clientProduct?.product}
            formData={data.clientPackageContentData}
            isActive={data.is_active}
            parentTreeData={data.parents}
            treeActiveStatus={data.treeActiveStatus}
            treeData={data.dataForTable}
            clientId={convertToInt(data.clientId)}
            clientPackageId={convertToInt(data.clientPackageId)}
            reloadTable={data.reloadTable}
            level={data.parentNames.length + 1}
            sport={convertToInt(data.parents.sportId)}
            id={data.id}
            clientPackageName={data.clientPackageName}
            backToIndexUrl={data.backToIndexUrl}
        />,
    });

    data.setState(() => ({
        openEditGlobeModal: true,
    }));
};

export const handleOpenEditModal = async (data) => {
    generateLinkForModals(data, 'clients.packages.content.index');
    await openEditModal(data);
};

export const openEditModal = async (data) => {
    showModal({
        size: 'small',
        isVisible: true,
        className: 'client-package-content-modal',
        content: <ContentPackageForm
            header={`Client '${data.clientName}' - ${data.clientPackageName} - Content\n${data.parentNames?.join(' > ')} > ${data.name}`}
            entityName={`${data.parentNames?.join(' > ')} > ${data.name}`}
            bookingTypes={data.ContentTreeData.bookingTypes}
            eventContentVariants={data.ContentTreeData.eventContentVariants}
            productVariants={await fetchProductEventContentVariants(data.clientProduct.product.id)}
            product={data.clientProduct.product}
            formData={data.clientPackageContentData}
            isActive={data.is_active}
            parentTreeData={data.parents}
            treeActiveStatus={data.treeActiveStatus}
            treeData={data.dataForTable}
            clientId={convertToInt(data.clientId)}
            clientPackageId={convertToInt(data.clientPackageId)}
            reloadTable={data.reloadTable}
            level={data.parentNames?.length + 1}
            backToIndexUrl={data.backToIndexUrl}
        />,
    });
};

export const generateLinkForModals = (data, path) => {
    if (!data.parents) {
        return openErrorModal(data);
    }

    const urlParams = [];

    data.parents.sportId && urlParams.push('sport_id=' + convertToInt(data.parents.sportId));
    data.parents.content_tier && urlParams.push('content_tier_id=' + convertToInt(data.parents.content_tier));
    data.parents.tournament_category && urlParams.push('tournament_category_id=' + convertToInt(data.parents.tournament_category));
    data.parents.property_licence && urlParams.push('property_licence_id=' + convertToInt(data.parents.property_licence));
    data.parents.tournament && urlParams.push('tournament_id=' + convertToInt(data.parents.tournament));
    data.parents.stage_category && urlParams.push('stage_category_id=' + convertToInt(data.parents.stage_category));
    data.parents.property_licence && data.parents.content_category && !data.children && urlParams.push('content_category_lvl3=' + convertToInt(data.parents.content_category));
    data.parents.property_licence && data.parents.content_category && data.children && urlParams.push('content_category_lvl2=' + convertToInt(data.parents.content_category));
    !data.parents.property_licence && data.parents.content_tier && data.parents.content_category && 2 === data.parentNames.length && urlParams.push('content_category_lvl2=' + convertToInt(data.parents.content_category));
    !data.parents.property_licence && data.parents.content_tier && data.parents.content_category && 1 === data.parentNames.length &&  urlParams.push('content_category_lvl1=' + convertToInt(data.parents.content_category));

    data.history.push({
        pathname: getLink(path, {
            id: data.clientId,
            packagesId: data.clientPackageId,
        }),
        search: '?' + urlParams.join('&'),
    });
};

export const openErrorModal = (data) => {
    return showErrorModal({
        header: 'Page not found',
        text: 'Please check the url and try again',
        errorButtonText: 'Back to packages content table',
        hideButtons: false,
        redirectLink: getLink('clients.packages.content.index',
            {
                id: data.clientId,
                packagesId: data.clientPackageId,
            }),
        errorButtonIcon: 'arrow circle left',
    });
};

const getResult = (data, expectedValue) => {
    if (!Array.isArray(data)) return [];

    return data.flatMap((item) => item.children.filter((child) => child.value === expectedValue.toString()));
};

export const resolveNonSportradarContent = (params, dataForTable) => {
    const {
        content_category_lvl1,
        content_category_lvl2,
        content_category_lvl3,
        content_tier_id,
        property_licence_id,
    } = params;

    let result = null;

    if (content_category_lvl1) {
        result = dataForTable.find((item) => item.value === content_category_lvl1.toString());
        result = findByParent(result, 'content_tier', content_tier_id);
    }

    if (content_category_lvl2) {
        let list = getResult(dataForTable, content_tier_id);
        list = getResult(list, content_category_lvl2);

        if (property_licence_id) {
            list = getResult(list, property_licence_id);
        }
        result = list[0] ?? null;
    }

    if (content_category_lvl3) {
        let list = getResult(dataForTable, content_tier_id);
        list = list.flatMap((item) => getResult(item.children, property_licence_id));
        list = getResult(list, content_category_lvl3);
        result = list[0] ?? null;
    }

    return result;
};

const findByParent = (data, key, expectedValue) => {
    if (!data?.children || null == expectedValue) return null;

    return data.children.find((item) => item.parents?.[key] === expectedValue.toString()) ?? null;
};

export const resolveSportradarContent = (params, dataForTable) => {
    const {
        sport_id,
        content_tier_id,
        tournament_category_id,
        property_licence_id,
        tournament_id,
    } = params;

    let result = dataForTable.find((item) => item.value === sport_id.toString());

    if (content_tier_id && result?.children) {
        result = findByParent(result, 'content_tier', content_tier_id);

        if (tournament_category_id) {
            result = findByParent(result, 'tournament_category', tournament_category_id);

            if (property_licence_id) {
                result = findByParent(result, 'property_licence', property_licence_id);

                if (tournament_id) {
                    result = findByParent(result, 'tournament', tournament_id);
                }
            }
        } else if (property_licence_id && result?.children) {
            result = result.children.map((item) => {
                let found = findByParent(item, 'property_licence', property_licence_id);

                if (tournament_id) {
                    found = findByParent(found, 'tournament', tournament_id);
                }

                return found;
            }).find(Boolean) ?? null;
        }
    }

    return result;
};

export const resolveStageContent = (params, dataForTable) => {
    const {
        sport_id,
        content_tier_id,
        property_licence_id,
        stage_category_id,
    } = params;

    let result = dataForTable.find((item) => item.value === sport_id.toString());

    if (content_tier_id && result?.children) {
        result = findByParent(result, 'content_tier', content_tier_id);

        if (stage_category_id && result?.children) {
            let stageResult = findByParent(result, 'stage_category', stage_category_id);

            if (stageResult) {
                result = stageResult;

                if (property_licence_id) {
                    result = findByParent(result, 'property_licence', property_licence_id);
                }
            } else if (property_licence_id) {
                result = result.children.map((item) => {
                    let found = findByParent(item, 'property_licence', property_licence_id);

                    if (found) {
                        found = findByParent(found, 'stage_category', stage_category_id);
                    }

                    return found;
                }).find(Boolean) ?? null;
            }
        }
    }

    return result;
};

